import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import { Modal, Button, Table } from 'react-bootstrap';

import { theme } from 'components/themes/v1';
import { readNotification, getPaginatedNotif, readAllNotification } from 'services/notification';
import { TARGET_TYPE_APPLICATION } from 'constants/notification';
import { storeAdminLatestNotif, storeAdminUnreadNotif, storeAdminPaginatedNotif, storeAdminCurrentPage, storeAdminLastPage, storeAdminScrollPosition } from 'store/actions/notification';
import { DateToShortDateTime } from 'utils/datetime';
import NotificationModal from 'components/common/NotificationModal';

import { UserNotification } from '.';

const Notification = styled.div`
  position: relative;
  width: 100%;
`;

const NotificationHeader = styled.div`
  font-size: 0.85rem;
  font-weight: bold;
  text-align: center;
  width: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors.divider};
  padding: 10px 8px;
`;

const NotificationBody = styled.div`
  width: 100%;
  margin-bottom: 14px;
  height: 300px;
  overflow-y: auto;
`;

const NotificationFooter = styled.div`
  text-align: center;

  & a:hover {
    color: ${(props) => props.theme.colors.red};
  }
`;

const NotificationItem = styled.a`
  display: block;
  position: relative;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.85rem;
  padding: 8px 12px;
  background-color: ${(props) => props.theme.colors.white};
  border-bottom: 1px solid ${(props) => props.theme.colors.divider};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    text-decoration: none;
    transition: all 0.3s ease-in-out;
  }
`;

const NotificationItemBadge = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};
  position: absolute;
  background-color: ${theme.colors.danger};
  width: 6px;
  height: 6px;
  border-radius: 50%;
  right: 10px;
  top: 10px;
`;

const NotificationBadgeSmall = styled.span`
  display: inline-block;
  background-color: ${theme.colors.danger};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.primary};
  }
`;

const ModalPagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  padding: 0;
  
  button {
    padding: 2px 6px;
    border: 1px solid ${(props) => props.theme.colors.divider};
    background: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 0.85rem;
    
    &:hover {
      background: ${(props) => props.theme.colors.gray100};
    }
    
    &.active {
      background: ${theme.colors.primary};
      color: white;
      border-color: ${theme.colors.primary};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const NotificationModalTable = styled(Table)`
  font-size: 0.85rem;
  th {
    background-color: ${(props) => props.theme.colors.gray100};
    font-weight: 600;
  }
`;

const ModalLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;


const AdminNotification = (props) => {
  const notifData = useSelector((state) => state.adminNotif.latest);
  const notifUnread = useSelector((state) => state.adminNotif.unread);
  const reduxNotifData = useSelector((state) => state.adminNotif.paginatedData);
  const reduxCurrentPage = useSelector((state) => state.adminNotif.currentPage);
  const reduxLastPage = useSelector((state) => state.adminNotif.lastPage);
  const reduxScrollPosition = useSelector((state) => state.adminNotif.scrollPosition);
  const userData = useSelector((state) => state.account.detail);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const [paginatedNotifData, setPaginatedNotifData] = useState(reduxNotifData);
  const [currentPage, setCurrentPage] = useState(reduxCurrentPage);
  const [lastPage, setLastPage] = useState(reduxLastPage);
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  // Modal states
  const [showAllModal, setShowAllModal] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [hoveredUnreadId, setHoveredUnreadId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch paginated notifications
  const fetchNotifications = async (page = 1) => {
    try {
      setIsLoading(true);
      const resp = await getPaginatedNotif({ page, per_page: 10 });
      
      if (resp.success && resp.data && resp.data.notifications) {
        const notificationsData = resp.data.notifications;
        const newNotifications = notificationsData.data || [];
        const lastPageNum = notificationsData.last_page;
        
        if (!lastPageNum) {
          console.error('last_page not found in response');
          return;
        }
        
        let updatedData = newNotifications;
        if (page === 1) {
          setPaginatedNotifData(newNotifications);
        } else {
          updatedData = [...paginatedNotifData, ...newNotifications];
          setPaginatedNotifData(updatedData);
        }
        
        setCurrentPage(page);
        setLastPage(lastPageNum);
        
        dispatch(storeAdminPaginatedNotif(updatedData));
        dispatch(storeAdminCurrentPage(page));
        dispatch(storeAdminLastPage(lastPageNum));
      } else {
        console.error('Failed to fetch paginated notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error(t('http.request_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all notifications for modal
  const fetchAllNotifications = async (page = 1, perPage = 10) => {
    try {
      setModalLoading(true);
      const resp = await getPaginatedNotif({ page, per_page: perPage });
      
      if (resp.success && resp.data && resp.data.notifications) {
        const notificationsData = resp.data.notifications;
        const notifications = notificationsData.data || [];
        const totalPages = notificationsData.last_page;
        
        setAllNotifications(notifications);
        setModalTotalPages(totalPages);
      }
    } catch (error) {
      console.error('Error fetching all notifications:', error);
      toast.error(t('http.request_failed'));
    } finally {
      setModalLoading(false);
    }
  };

  // Load notifications on mount
  useEffect(() => {
    if (paginatedNotifData && paginatedNotifData.length > 0) {
      setPaginatedNotifData(reduxNotifData);
      setCurrentPage(reduxCurrentPage);
      setLastPage(reduxLastPage);
      
      if (scrollContainerRef.current && reduxScrollPosition > 0) {
        setTimeout(() => {
          scrollContainerRef.current.scrollTop = reduxScrollPosition;
        }, 0);
      }
    } else {
      fetchNotifications(1);
    }
  }, []);

  // Infinite scroll handler
  const handleScroll = (e) => {
    const container = e.target;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    
    if (isNearBottom && !isLoading && currentPage < lastPage) {
      const nextPage = currentPage + 1;
      fetchNotifications(nextPage);
      dispatch(storeAdminScrollPosition(container.scrollTop));
    }
  };

  const onClickNotif = async (notif, idx) => {
    if (notif.is_read) return;
    
    const payload = { notifications: [notif.id] };
    const resp = await readNotification(payload);

    if (resp.success) {
      const updatedData = [...paginatedNotifData];
      updatedData[idx].is_read = 1;
      setPaginatedNotifData(updatedData);
      dispatch(storeAdminPaginatedNotif(updatedData));
      dispatch(storeAdminUnreadNotif(notifUnread > 0 ? notifUnread - 1 : 0));

      if (!notif.target_type) {
        return;
      }

      switch (notif.target_type) {
        case TARGET_TYPE_APPLICATION:
          navigate(`/admin/candidates/${notif.table_ref_id}`);
        default:
          return;
      }
    } else {
      const errMsg = notif.data?.messages?.[0] || t('http.request_failed');
      toast.error(errMsg);
    }
  };

  // Handle modal page change
  const handleModalPageChange = (page) => {
    setModalPage(page);
    fetchAllNotifications(page, itemsPerPage);
  };

  // Get page numbers to show (max 5 with current in middle)
  const getPageNumbersToShow = () => {
    const maxButtons = 5;
    let startPage = Math.max(1, modalPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(modalTotalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  // Handle per-page change
  const handleItemsPerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setItemsPerPage(newPerPage);
    setModalPage(1);
    fetchAllNotifications(1, newPerPage);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (!userData?.id) {
      toast.error(t('http.request_failed'));
      return;
    }

    const payload = { user_id: userData.id };
    const resp = await readAllNotification(payload);

    if (resp.success) {
      dispatch(storeAdminUnreadNotif(0));
      setShowConfirmModal(false);
      toast.success(t('notification_marked_read') || 'All notifications marked as read');
      
      // Refresh all notifications
      fetchAllNotifications(modalPage, itemsPerPage);
    } else {
      const errMsg = resp.data?.messages?.[0] || t('http.request_failed');
      toast.error(errMsg);
    }
  };

  return (
    <Notification>
      <NotificationHeader>{t('menu.notifications')}</NotificationHeader>

      <NotificationBody ref={scrollContainerRef} onScroll={handleScroll}>
        {paginatedNotifData && paginatedNotifData.length > 0 ? (
          paginatedNotifData.map((notif, idx) => (
            <NotificationItem key={`notif${idx}`} onClick={() => onClickNotif(notif, idx)}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ flex: 1 }}>{notif.content}</span>
                  {!notif.is_read && <NotificationItemBadge show={true} />}
                </div>
                {notif.created_at && <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px', textAlign: 'right' }}>{DateToShortDateTime(notif.created_at, i18n.language)}</div>}
              </div>
            </NotificationItem>
          ))
        ) : (
          <NotificationItem style={{ textAlign: 'center', pointerEvents: 'none' }}>
            {t('notification_empty') || 'No notifications'}
          </NotificationItem>
        )}
      </NotificationBody>

      <NotificationFooter>
        <a href='#' onClick={(e) => { e.preventDefault(); setShowAllModal(true); fetchAllNotifications(1, itemsPerPage); }}>{t('see_all')}</a>
      </NotificationFooter>

      <NotificationModal
        show={showAllModal}
        onHide={() => setShowAllModal(false)}
        notifications={allNotifications}
        modalPage={modalPage}
        modalTotalPages={modalTotalPages}
        modalLoading={modalLoading}
        itemsPerPage={itemsPerPage}
        onPageChange={handleModalPageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onClose={() => setShowAllModal(false)}
        onMarkAllAsRead={() => setShowConfirmModal(true)}
        notifUnread={notifUnread}
        hoveredUnreadId={hoveredUnreadId}
        onHoveredUnreadIdChange={setHoveredUnreadId}
        onMarkAsRead={(notif) => {
          const payload = { notifications: [notif.id] };
          readNotification(payload).then((resp) => {
            if (resp.success) {
              dispatch(storeAdminUnreadNotif(notifUnread > 0 ? notifUnread - 1 : 0));
              fetchAllNotifications(modalPage, itemsPerPage);
            }
          });
        }}
        getPageNumbersToShow={getPageNumbersToShow}
        language={i18n.language}
        minStatusWidth='200px'
      />

      {/* Confirmation modal for mark all as read */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} backdrop='static' keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirmation') || 'Confirmation'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('mark_all_as_read_confirm') || 'Are you sure you want to mark all notifications as read?'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button variant='primary' onClick={handleMarkAllAsRead}>
            {t('confirm') || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Notification>
  );
};

export default AdminNotification;
